import { useNavigate, useParams } from "react-router-dom";
import Hero from "../components/DestinationIdComp/HeroSection";
import OverviewSection from "../components/DestinationIdComp/OverviewSection";
import PackagesSection, {
  type PackageEnquiryDraft,
} from "../components/DestinationIdComp/PackagesSection";
import SeasonSection from "../components/DestinationIdComp/SeasonSection";
import type { SeasonIconKey } from "../content/indiaSiteContent";
import { cmsApi } from "../services/cms";
import { useCMS } from "../hooks/useCMS";
import { buildPackageEnquiryMessage } from "../utils/packageEnquiry";

type BestTimeToVisit = {
  iconName?: string;
  iconColor?: string;
  bgColor?: string;
  color?: string;
  title: string;
  from: string;
  to: string;
  description: string;
  suggestion: string;
};

type Destination = {
  name: string;
  slug: string;
  description: string;
  shortDescription?: string;
  heroImageUrl?: string;
  thumbnailUrl?: string;
  media?: DestinationMediaPayload;
  keyHighlights?: string[];
  bestTimeToVisit?: BestTimeToVisit[];
};

type MediaValue = string | MediaItem | null | undefined;

type DestinationMediaPayload = {
  title_image?: MediaValue;
  titleImage?: MediaValue;
  gallery?: MediaValue[] | null;
  images?: MediaValue[] | null;
};

type MediaItem = {
  mediaUrl?: string | null;
  imageUrl?: string | null;
  fileUrl?: string | null;
  assetUrl?: string | null;
  url?: string | null;
  image?: string | null;
  src?: string | null;
  thumbnailUrl?: string | null;
  title_image?: MediaValue;
  titleImage?: MediaValue;
  gallery?: MediaValue[] | null;
  images?: MediaValue[] | null;
  media?: MediaItem | null;
};

type SeasonColorStyle = {
  color: string;
  bg: string;
};

const seasonColorStyles: Record<string, SeasonColorStyle> = {
  green:   { color: "text-green-600",   bg: "bg-green-50" },
  yellow:  { color: "text-yellow-600",  bg: "bg-yellow-50" },
  amber:   { color: "text-amber-600",   bg: "bg-amber-50" },
  orange:  { color: "text-orange-600",  bg: "bg-orange-50" },
  blue:    { color: "text-blue-600",    bg: "bg-blue-50" },
  indigo:  { color: "text-indigo-600",  bg: "bg-indigo-50" },
  teal:    { color: "text-teal-600",    bg: "bg-teal-50" },
  emerald: { color: "text-emerald-600", bg: "bg-emerald-50" },
  red:     { color: "text-red-600",     bg: "bg-red-50" },
  pink:    { color: "text-pink-600",    bg: "bg-pink-50" },
  purple:  { color: "text-purple-600",  bg: "bg-purple-50" },
};

const extractColorToken = (value?: string | null) => {
  const normalized = (value || "").trim().toLowerCase();
  if (!normalized) return "";
  const classMatch = normalized.match(/(?:text|bg)-([a-z]+)-\d{2,3}/);
  if (classMatch?.[1]) return classMatch[1];
  return normalized;
};

const resolveSeasonColors = (
  color?: string | null,
  bgColor?: string | null
): SeasonColorStyle => {
  const colorToken = extractColorToken(color);
  const bgToken = extractColorToken(bgColor);
  if (colorToken && seasonColorStyles[colorToken]) return seasonColorStyles[colorToken];
  if (bgToken && seasonColorStyles[bgToken]) return seasonColorStyles[bgToken];
  return seasonColorStyles.blue;
};

const resolveSeasonIconKey = (iconName?: string | null): SeasonIconKey => {
  const normalized = (iconName || "").trim().toLowerCase();
  if (normalized.includes("leaf") || normalized.includes("spring") || normalized.includes("dry")) return "leaf";
  if (normalized.includes("sun") || normalized.includes("summer")) return "sun";
  if (normalized.includes("autumn") || normalized.includes("fall") || normalized.includes("maple") || normalized.includes("wet") || normalized.includes("rain")) return "autumn";
  if (normalized.includes("snow") || normalized.includes("winter") || normalized.includes("festive")) return "snow";
  return "sun";
};

type DestinationPackageItem = {
  id: string;
  destination?: string | null;
  mainPackage?: {
    destination?: string | null;
    title?: string | null;
    websiteSlug?: string | null;
  };
  package?: {
    id?: string;
    packageId?: string;
    websiteSlug?: string;
    destination?: string | null;
    title?: string | null;
    name?: string;
    duration?: string;
    price?: string;
    idealFor?: string;
    description?: string;
  };
};

type MainPackageFeature = {
  iconName?: string | null;
  description?: string | null;
};

type MainPackageInclusion = {
  iconName?: string | null;
  description?: string | null;
};

type PackageInclusionValue = string | { iconName: string; description: string };

type MainPackageItem = {
  id: string;
  country?: string | null;
  websiteSlug?: string | null;
  title?: string | null;
  amount?: number | null;
  amountCurrency?: string | null;
  destination?: string | null;
  description?: string | null;
  highlights?: string[];
  features?: MainPackageFeature[];
  inclusions?: MainPackageInclusion[];
};

const siteRegion = (import.meta.env.VITE_SITE_REGION || "IN").toUpperCase();
const siteCountry = siteRegion === "AE" ? "United Arab Emirates" : "India";

const normalizeToken = (value?: string | null) =>
  (value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const toMediaUrl = (value?: MediaValue): string => {
  if (typeof value === "string") return value.trim();
  if (!value) return "";
  return (
    value.mediaUrl ||
    value.imageUrl ||
    value.fileUrl ||
    value.assetUrl ||
    value.url ||
    value.image ||
    value.src ||
    value.thumbnailUrl ||
    toMediaUrl(value.media) ||
    ""
  ).trim();
};

const uniqueMediaUrls = (items: MediaValue[]) =>
  Array.from(new Set(items.map(toMediaUrl).filter(Boolean)));

const getGalleryUrls = (payload?: DestinationMediaPayload | MediaItem | null) =>
  uniqueMediaUrls([...(payload?.gallery || []), ...(payload?.images || [])]);

const getTitleImageUrls = (payload?: DestinationMediaPayload | MediaItem | null) =>
  uniqueMediaUrls([payload?.title_image, payload?.titleImage]);

const formatPackagePrice = (amount?: number | null, currency?: string | null) => {
  if (typeof amount !== "number") return "Contact Us";
  const normalizedCurrency = (currency || "INR").toUpperCase();
  return `Starting from ${normalizedCurrency} ${amount.toLocaleString("en-IN")} per person`;
};

const getPackageDuration = (features?: MainPackageFeature[]) => {
  if (!Array.isArray(features) || features.length === 0) return "7 Days";
  const featureByIcon = features.find((f) => (f.iconName || "").toLowerCase().includes("timer"));
  if (featureByIcon?.description?.trim()) return featureByIcon.description.trim();
  const featureByText = features.find((f) => /\b(day|days|night|nights)\b/i.test(f.description || ""));
  if (featureByText?.description?.trim()) return featureByText.description.trim();
  return "7 Days";
};

const getPackageIdealFor = (features?: MainPackageFeature[]) => {
  if (!Array.isArray(features) || features.length === 0) return "All";
  const preferredFeature = features.find((f) => /(shopping|bag|suitcase|star|medal|travel)/i.test(f.iconName || ""));
  if (preferredFeature?.description?.trim()) return preferredFeature.description.trim();
  const firstFeatureText = features.find((f) => (f.description || "").trim());
  return firstFeatureText?.description?.trim() || "All";
};

const toPackageFeatures = (
  features?: MainPackageFeature[],
  fallbackDuration?: string,
  fallbackIdealFor?: string,
) => {
  const mappedFeatures = (features || [])
    .map((feature) => ({
      iconName: feature.iconName?.trim() || undefined,
      description: feature.description?.trim() || "",
    }))
    .filter((feature) => feature.description);

  if (mappedFeatures.length > 0) return mappedFeatures;

  return [
    { iconName: "timer", description: fallbackDuration?.trim() || "" },
    { iconName: "suitcase-rolling", description: fallbackIdealFor?.trim() || "" },
  ].filter((feature) => feature.description);
};

const toPackageInclusions = (inclusions?: MainPackageInclusion[]): PackageInclusionValue[] => {
  if (!Array.isArray(inclusions)) return [];
  return inclusions
    .map((item) => {
      const description = item.description?.trim() || "";
      if (!description) return null;

      const iconName = item.iconName?.trim() || "";
      return iconName ? { iconName, description } : description;
    })
    .filter((item): item is PackageInclusionValue => Boolean(item));
};

const isMatchingDestinationPackage = (pkg: MainPackageItem, destination: Destination) => {
  const packageDestination = normalizeToken(pkg.destination);
  const destinationName = normalizeToken(destination.name);
  const destinationSlug = normalizeToken(destination.slug);
  const packageTitle = normalizeToken(pkg.title);

  if (packageDestination) {
    if (packageDestination === destinationName || packageDestination === destinationSlug) return true;
    if (packageDestination.includes(destinationName) || destinationName.includes(packageDestination)) return true;
  }
  if (packageTitle) {
    if (packageTitle.includes(destinationName) || packageTitle.includes(destinationSlug)) return true;
  }
  return false;
};

function Destination() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const { data: destination, loading } = useCMS<Destination>(() => cmsApi.getDestinationBySlug(slug!));
  const { data: media } = useCMS<MediaItem[]>(() => cmsApi.getDestinationMedia(slug!));
  const { data: destinationPackages } = useCMS<DestinationPackageItem[]>(() => cmsApi.getDestinationPackages(slug!));
  const { data: mainPackages } = useCMS<MainPackageItem[]>(() => cmsApi.getMainPackages(siteCountry));

  if (loading) return <div className="py-20 text-center">Loading...</div>;
  if (!destination) return <div className="py-20 text-center">Destination not found</div>;

  const mappedMainPackages = (mainPackages || [])
    .filter((pkg) => isMatchingDestinationPackage(pkg, destination))
    .map((pkg) => {
      const duration = getPackageDuration(pkg.features);
      const idealFor = getPackageIdealFor(pkg.features);

      return {
        id: pkg.websiteSlug?.trim() || pkg.id,
        title: pkg.title?.trim() || `${destination.name} Package`,
        destination: pkg.destination?.trim(),
        duration,
        price: formatPackagePrice(pkg.amount, pkg.amountCurrency),
        idealFor,
        highlights: Array.isArray(pkg.highlights)
          ? pkg.highlights.map((item) => item?.trim() || "").filter(Boolean)
          : [],
        inclusions: toPackageInclusions(pkg.inclusions),
        summary: pkg.description?.trim() || `${destination.name} curated package`,
        features: toPackageFeatures(pkg.features, duration, idealFor),
      };
    });

  const mappedDestinationPackages = (destinationPackages || []).map((pkg) => {
    const duration = pkg.package?.duration || "7 Days";
    const idealFor = pkg.package?.idealFor || "All";

    return {
      id:
        pkg.package?.websiteSlug?.trim() ||
        pkg.package?.id?.trim() ||
        pkg.package?.packageId?.trim() ||
        pkg.id,
      destination:
        pkg.package?.destination?.trim() ||
        pkg.mainPackage?.destination?.trim() ||
        pkg.destination?.trim(),
      title: pkg.package?.name || pkg.package?.title || "Package",
      duration,
      price: pkg.package?.price || "Contact Us",
      idealFor,
      highlights: [],
      inclusions: [],
      summary: pkg.package?.description || "",
      features: toPackageFeatures(undefined, duration, idealFor),
    };
  });

  const packagesList = mappedMainPackages.length > 0 ? mappedMainPackages : mappedDestinationPackages;

  const destinationGalleryUrls = getGalleryUrls(destination.media);
  const endpointGalleryUrls = Array.isArray(media)
    ? uniqueMediaUrls(media)
    : getGalleryUrls(media);
  const titleImageUrls = uniqueMediaUrls([
    ...getTitleImageUrls(destination.media),
    ...(!Array.isArray(media) ? getTitleImageUrls(media) : []),
  ]);
  const primaryDestinationImage =
    destination.heroImageUrl ||
    destination.thumbnailUrl ||
    titleImageUrls[0] ||
    destinationGalleryUrls[0] ||
    endpointGalleryUrls[0] ||
    "";
  const overviewImageUrls = uniqueMediaUrls([
    ...destinationGalleryUrls,
    ...endpointGalleryUrls,
    ...titleImageUrls,
  ]);
  const overviewImages = overviewImageUrls.map((src, index) => ({
    src,
    alt: `${destination.name} view ${index + 1}`,
  }));

  const handleProceedToEnquiry = (draft: PackageEnquiryDraft) => {
    if (!destination) return;

    const packageSummary = {
      destinationName: destination.name,
      packageTitle: draft.selectedPackage.title,
      duration: draft.selectedPackage.duration,
      price: draft.selectedPackage.price,
      idealFor: draft.selectedPackage.idealFor,
      travellers: draft.travellers,
      travelMonth: draft.travelMonth,
      notes: draft.notes,
    };

    navigate("/destination#custom-itinerary", {
      state: {
        openCustomItinerary: true,
        enquiryPrefill: {
          subject: "package",
          message: buildPackageEnquiryMessage(packageSummary),
          packageSummary,
        },
      },
    });
  };

  const handleViewPackage = (selectedPackage: any) => {
    const routeId = selectedPackage?.id?.toString().trim();
    navigate(routeId ? `/packages/${routeId}` : "/packages");
  };

  return (
    <>
      <Hero
        name={destination.name}
        backgroundImage={primaryDestinationImage}
        archImage={destination.thumbnailUrl || titleImageUrls[0] || primaryDestinationImage}
        description={destination.description}
        shortDescription={destination.shortDescription || destination.description}
        backgroundVideo=""
      />
      <OverviewSection
        name={destination.name}
        title={destination.name}
        paragraphs={[destination.description]}
        keyHighlights={destination.keyHighlights || []}
        images={overviewImages}
      />
      {packagesList.length > 0 && (
        <PackagesSection
          key={destination.slug}
          destinationName={destination.name}
          packages={packagesList}
          onProceedToEnquiry={handleProceedToEnquiry}
          onViewPackage={handleViewPackage}
        />
      )}
      <SeasonSection
        destinationName={destination.name}
        heading="Best Time to Visit"
        subheading={`Plan your trip to ${destination.name}`}
        highlights={
          destination.bestTimeToVisit?.map((season) => {
            const palette = resolveSeasonColors(season.color, season.bgColor || season.iconColor);
            return {
              title: season.title,
              duration: `${season.from} - ${season.to}`,
              description: season.description,
              tag: season.suggestion,
              color: palette.color,
              bg: palette.bg,
              iconName: season.iconName || undefined,
              iconKey: resolveSeasonIconKey(season.iconName),
            };
          }) || []
        }
      />
    </>
  );
}

export default Destination;
